/**
 * WaitingQueue — the pinned pane's primary view: only the things waiting on a
 * person, in the server's order (longest wait first), split into two groups.
 *
 * Shared by ora and the Outlook add-in (`@product-app/common/workflows/…`);
 * there is no forked copy under the add-in tree (design Constraint 20).
 *
 * TWO GROUPS, NOT ONE FLAT LIST (operator, 2026-08-26: "I only want to bug
 * people when they are needed"):
 *
 *   "Needs you" — rows the server marked `needs_you`. This is the group the
 *                 waiting-on-you badge in `hitl-notifications` counts, so its
 *                 length IS the badge's number, visible directly beneath it.
 *   "Waiting"   — parked and visible, but not something the AM is being
 *                 interrupted for (a `warm_hitl` inside its grace window, or a
 *                 run on a workflow id outside NOTIFYING_WORKFLOW_IDS).
 *
 * `needs_you` is the SERVER's verdict, computed by `is_notifiable` in
 * `tools/waiting_queue.py` — the same module the badge's SQL comes from. This
 * component never re-derives it: a browser-side re-derivation is a second rule
 * by construction, and two rules that are never compared are one bug waiting.
 *
 * THREE LIST STATES:
 *   items          — the groups above
 *   nothing        — NOTHING is rendered (story 28633: no "Needs you · 0"
 *                    heading, no empty box, and no "Checking…" placeholder
 *                    before the first read — an empty section is noise in a
 *                    440px pane)
 *   could-not-load — "Couldn't refresh — last updated HH:MM"
 * A stale queue is NEVER presented as an accurate one, and a stale EMPTY queue
 * still shows its notice: "nothing is waiting" and "we could not find out what
 * is waiting" are different statements (AC-3), so a failed read is never
 * rendered as the silence that means "nothing".
 *
 * The could-not-load state has TWO wordings, chosen by `staleReason`, because
 * it has two causes. "Couldn't refresh" asserts that something went wrong; say
 * it while the poll is merely between ticks and the notice stops meaning
 * anything, which is worse than not showing one. A queue that is only OLD says
 * so plainly and accuses nothing.
 *
 * PLACEMENT: with rows on screen the notice rides at the END of the
 * "Needs you · N" heading line rather than in a banner above it — in a 440x920
 * pane vertical space is the scarce resource and a full-width banner costs a
 * visible queue row. With no rows there is no heading to share, and nothing
 * competing for the height, so there it stands alone.
 *
 * Text uses the pane's three styles only (`paneTypography`).
 */

import React from "react";
import CustomTooltip from "~/common/components/CustomTooltip";
import { formatTime } from "../formatTime";
import type { WaitingQueueStaleReason } from "../hooks/useWaitingQueue";
import { TYPE_BODY, TYPE_LABEL } from "../paneTypography";
import type { WaitingQueueItem } from "../types";
import { WaitingQueueRow } from "./WaitingQueueRow";

export const QUEUE_NEEDS_YOU_HEADING = "Needs you";
export const QUEUE_WAITING_HEADING = "Waiting";

/**
 * The stale notice's words.
 *
 * `"error"` — a fetch FAILED: "Couldn't refresh — last updated 2:45 pm", or
 * without the clause when nothing has ever succeeded (nothing truthful to
 * name). `"age"` — nothing failed; the queue is simply older than the window:
 * "Last updated 2:45 pm", which reports without accusing.
 *
 * Defaults to the failure wording so an existing caller that passes no reason
 * keeps the stricter sentence rather than silently downgrading a real failure.
 */
export function staleCopy(
  lastSuccessAt: number | null | undefined,
  reason: WaitingQueueStaleReason | null = "error",
): string {
  const at = lastSuccessAt
    ? formatTime(new Date(lastSuccessAt).toISOString())
    : null;
  if (reason === "age") {
    return at ? `Last updated ${at}` : "Not updated yet";
  }
  return at
    ? `Couldn't refresh — last updated ${at}`
    : "Couldn't refresh — no successful update yet";
}

export interface WaitingQueueProps {
  /** The server's rows, in the server's order. */
  items: WaitingQueueItem[];
  /**
   * True when the last fetch failed or nothing has succeeded for longer than
   * the staleness window. Renders the could-not-load state ABOVE any rows
   * already on screen — a failed fetch never clears them.
   */
  isStale?: boolean;
  /** Why the queue is stale, which selects the wording. Omitted defaults to
   *  the failure sentence — never downgrade an unexplained stale to "age". */
  staleReason?: WaitingQueueStaleReason | null;
  /** Epoch ms of the last SUCCESSFUL fetch — named in the stale copy. */
  lastSuccessAt?: number | null;
  /** Raised to the host. The queue never navigates by itself. */
  onOpenItem: (instanceId: string) => void;
  /** Injected only so tests and the render probe can pin "now". */
  now?: Date;
}

function GroupHeading({
  heading,
  count,
  testId,
  note,
}: {
  heading: string;
  count: number;
  testId: string;
  /** Rendered at the END of the heading line. The pane is a 440x920 strip and
   *  vertical space is the scarce resource, so a short status shares this row
   *  instead of claiming a full-width one above the queue. */
  note?: React.ReactNode;
}): React.ReactElement {
  return (
    // Sticky (story 28633): the pane's sections share one scroll area, and a
    // section's heading stays at its top while its rows scroll under it, so
    // the AM always sees which group they are in. Pane background behind it.
    <div className="sticky top-0 z-10 mb-1 flex items-baseline justify-between gap-2 bg-primary-gray-light-50 py-1">
      <h3
        data-testid={`${testId}-heading`}
        className={`shrink-0 ${TYPE_LABEL} text-primary-gray-light-500`}
      >
        {heading} · {count}
      </h3>
      {note}
    </div>
  );
}

function Group({
  heading,
  items,
  onOpenItem,
  now,
  testId,
  alwaysShow = false,
  featureFirst = false,
  note,
}: {
  heading: string;
  items: WaitingQueueItem[];
  onOpenItem: (instanceId: string) => void;
  now?: Date;
  testId: string;
  alwaysShow?: boolean;
  featureFirst?: boolean;
  note?: React.ReactNode;
}): React.ReactElement | null {
  if (items.length === 0 && !alwaysShow) return null;
  return (
    <section data-testid={testId} className="flex flex-col">
      {/* The count lives in the header TEXT so the correspondence with the
          badge is visible rather than inferred, and so group membership is
          never carried by colour or position alone. */}
      <GroupHeading
        heading={heading}
        count={items.length}
        testId={testId}
        note={note}
      />
      {items.length === 0 ? null : (
        <ul
          aria-label={heading}
          className="flex list-none flex-col gap-2 p-0"
        >
          {items.map((item, index) => (
            <WaitingQueueRow
              key={item.instance_id}
              item={item}
              onOpenItem={onOpenItem}
              now={now}
              featured={featureFirst && index === 0}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function WaitingQueue({
  items,
  isStale = false,
  staleReason = "error",
  lastSuccessAt = null,
  onOpenItem,
  now,
}: WaitingQueueProps): React.ReactElement | null {
  // Nothing waiting (or nothing read yet) and nothing to warn about: render
  // nothing at all. A stale queue is the exception — see the header.
  if (items.length === 0 && !isStale) return null;

  // Order is the SERVER's order. This partition preserves it inside each
  // group and the component does NOT re-sort — one ordering rule (the SQL)
  // rather than two that can disagree.
  const needsYou = items.filter((i) => i.needs_you);
  const waiting = items.filter((i) => !i.needs_you);

  // Shares the "Needs you · N" line rather than claiming a row of its own.
  // Truncates, with the full sentence on hover, so a long status can never
  // wrap and grow the header it is sitting in.
  // The full sentence is ora's CustomTooltip, not a native title (story 28633).
  const staleText = staleCopy(lastSuccessAt, staleReason);
  const staleNote = isStale ? (
    <CustomTooltip
      id="waiting-queue-stale"
      title={staleText}
      placement="bottom-start"
      arrow
      isOnModal
    >
      <span
        role="status"
        data-testid="waiting-queue-stale"
        className={`min-w-0 truncate ${TYPE_BODY} text-primary-gray-light-500`}
      >
        {staleText}
      </span>
    </CustomTooltip>
  ) : null;

  return (
    // Rows stay legible at 320px: one column, no fixed widths, no table.
    <div
      className="flex w-full flex-col gap-3"
      data-testid="waiting-queue"
      data-item-count={items.length}
    >
      {items.length > 0 ? (
        <>
          <Group
            heading={QUEUE_NEEDS_YOU_HEADING}
            items={needsYou}
            onOpenItem={onOpenItem}
            now={now}
            testId="waiting-queue-needs-you"
            alwaysShow
            featureFirst
            note={staleNote}
          />
          <Group
            heading={QUEUE_WAITING_HEADING}
            items={waiting}
            onOpenItem={onOpenItem}
            now={now}
            testId="waiting-queue-waiting"
          />
        </>
      ) : (
        // No heading row to share, and no rows competing for the height, so
        // here the notice stands on its own. Never silence (AC-3): a queue we
        // could not read is not a queue known to be empty.
        <CustomTooltip
          id="waiting-queue-stale"
          title={staleText}
          placement="bottom"
          arrow
          isOnModal
        >
          <p
            role="status"
            data-testid="waiting-queue-stale"
            className={`px-3 py-3 text-center ${TYPE_BODY} text-primary-gray-light-500`}
          >
            {staleText}
          </p>
        </CustomTooltip>
      )}
    </div>
  );
}

export default WaitingQueue;
